import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Copy, Search, Download, Upload, Eye, EyeOff, ExternalLink } from "lucide-react";
import { Input } from "@/components/ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, API } from "@/lib/api";
import { formatRupiah, getThumbImage, stockInfo } from "@/lib/helpers";
import ProductForm from "@/pages/admin/ProductForm";

export default function ProductsManager() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const fileRef = useRef();

  const load = () => {
    setLoading(true);
    api.get("/admin/products", { params: q ? { q } : {} }).then((r) => setProducts(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => {
    api.get("/categories", { params: { all: true } }).then((r) => setCategories(r.data)).catch(() => {});
    api.get("/brands", { params: { all: true } }).then((r) => setBrands(r.data)).catch(() => {});
  }, []);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [q]); // eslint-disable-line

  const openNew = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (p) => { setEditing(p); setFormOpen(true); };

  const openExactMatch = async () => {
    const query = q.trim();
    if (!query) return;
    try {
      const { data } = await api.get("/admin/products", { params: { q: query } });
      const exact = data.find((p) => (p.code || "").toLowerCase() === query.toLowerCase() || (p.name || "").toLowerCase() === query.toLowerCase());
      if (exact) {
        setProducts(data);
        openEdit(exact);
      } else {
        toast.info(`Tidak ada produk dengan kode/nama persis "${query}"`);
      }
    } catch { toast.error("Gagal mencari produk"); }
  };

  const duplicate = async (p) => { try { await api.post(`/admin/products/${p.id}/duplicate`); toast.success("Produk digandakan"); load(); } catch { toast.error("Gagal"); } };
  const toggleActive = async (p) => { try { await api.patch(`/admin/products/${p.id}/toggle-active`); load(); } catch { toast.error("Gagal"); } };
  const confirmDelete = async () => { try { await api.delete(`/admin/products/${toDelete.id}`); toast.success("Produk dihapus"); load(); } catch { toast.error("Gagal"); } finally { setToDelete(null); } };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/products-export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "produk-upplesories.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Gagal export"); }
  };

  const importCsv = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/products-import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Import selesai: ${data.created} baru, ${data.updated} diperbarui`); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal import"); }
    finally { e.target.value = ""; }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Produk</h1>
          <p className="mt-1 text-sm text-muted-foreground">{products.length} produk dalam katalog.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={exportCsv} data-testid="export-csv-btn" className="flex items-center gap-1.5 rounded-xl border border-border px-3 py-2.5 text-sm hover:bg-secondary"><Download className="h-4 w-4" /> Export</button>
          <button onClick={() => fileRef.current?.click()} data-testid="import-csv-btn" className="flex items-center gap-1.5 rounded-xl border border-border px-3 py-2.5 text-sm hover:bg-secondary"><Upload className="h-4 w-4" /> Import</button>
          <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={importCsv} />
          <button onClick={openNew} data-testid="admin-add-product-btn" className="flex items-center gap-1.5 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90"><Plus className="h-4 w-4" /> Tambah Produk</button>
        </div>
      </div>

      <div className="relative mt-5 max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          data-testid="admin-product-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); openExactMatch(); } }}
          placeholder="Cari nama atau kode produk... (Enter = buka yang cocok persis)"
          className="pl-9"
        />
      </div>

      <div className="mt-5 space-y-2">
        {loading ? <p className="text-sm text-muted-foreground">Memuat...</p> : products.length === 0 ? (
          <p className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">Belum ada produk.</p>
        ) : products.map((p) => {
          const stock = stockInfo(p.stock_status);
          const hasPromo = p.promo_price && p.promo_price < p.price;
          return (
            <div key={p.id} data-testid={`admin-product-row-${p.id}`} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
              <img src={getThumbImage(p)} alt={p.name} loading="lazy" decoding="async" className="h-16 w-16 shrink-0 rounded-lg object-cover" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="truncate font-semibold">{p.name}</p>
                  {!p.active && <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] text-muted-foreground">Nonaktif</span>}
                </div>
                <p className="text-xs text-muted-foreground">{p.code} · {p.category_name || "-"} · <span className={`rounded px-1.5 py-0.5 ${stock.cls}`}>{stock.label}</span></p>
                <p className="mt-0.5 text-sm font-bold">{formatRupiah(hasPromo ? p.promo_price : p.price)} {hasPromo && <span className="text-xs text-muted-foreground line-through">{formatRupiah(p.price)}</span>}</p>
              </div>
              <div className="flex shrink-0 gap-1">
                <a href={`/produk/${p.slug}`} target="_blank" rel="noreferrer" className="hidden sm:block rounded-lg p-2 hover:bg-secondary" title="Lihat"><ExternalLink className="h-4 w-4" /></a>
                <button onClick={() => toggleActive(p)} className="rounded-lg p-2 hover:bg-secondary" title="Aktif/Nonaktif" data-testid={`toggle-active-${p.id}`}>{p.active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}</button>
                <button onClick={() => duplicate(p)} className="rounded-lg p-2 hover:bg-secondary" title="Gandakan" data-testid={`duplicate-${p.id}`}><Copy className="h-4 w-4" /></button>
                <button onClick={() => openEdit(p)} className="rounded-lg p-2 hover:bg-secondary" title="Edit" data-testid={`edit-product-${p.id}`}><Pencil className="h-4 w-4" /></button>
                <button onClick={() => setToDelete(p)} className="rounded-lg p-2 text-destructive hover:bg-destructive/10" title="Hapus" data-testid={`delete-product-${p.id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          );
        })}
      </div>

      <ProductForm open={formOpen} onClose={() => setFormOpen(false)} product={editing} categories={categories} brands={brands} onSaved={load} />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Hapus produk?</AlertDialogTitle><AlertDialogDescription>Produk "{toDelete?.name}" akan dihapus permanen.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction data-testid="confirm-delete-product" onClick={confirmDelete} className="bg-destructive">Hapus</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
